import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  FileSpreadsheet, 
  FileText,
  Calendar as CalendarIcon,
  Filter,
  Printer,
  CalendarDays
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PageHeader from '../components/headers/Header';
import { vacationPlanningData } from '../services/leavesAPIs';
import { getAllEmployees } from '../services/HRstaff';

const DAY_MS = 24 * 60 * 60 * 1000;

const STATUS_META = {
  PENDING: { letter: 'P', bg: '#FFFBEB', color: '#B45309', border: '#FDE68A', label: 'Pending' },
  APPROVED: { letter: 'A', bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', label: 'Approved' },
  // REJECTED: { letter: 'R', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA', label: 'Rejected' }
};

const STATUS_PRIORITY = { APPROVED: 1, REJECTED: 2, PENDING: 3 };
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const HEADER_ROW_HEIGHTS = [44, 40, 32, 32];
const HEADER_ROW_TOPS = [0, 44, 84, 116];

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      return new Date(year, month - 1, day);
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeDate = (value) => {
  const d = toDateSafe(value);
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
};

const formatDate = (value) => {
  const d = toDateSafe(value);
  return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
};

const dateToKey = (date) => {
  const d = normalizeDate(date);
  return d ? d.toISOString().slice(0, 10) : '';
};

const toIsoDateParam = (value) => {
  const d = normalizeDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseMonthInput = (monthValue, isEnd = false) => {
  if (!monthValue) return null;
  const [yearStr, monthStr] = monthValue.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!year || !month) return null;
  if (isEnd) return new Date(year, month, 0);
  return new Date(year, month - 1, 1);
};

const toMonthInput = (date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${y}-${m}`;
};

const getIsoWeek = (dateInput) => {
  const date = normalizeDate(dateInput);
  if (!date) return null;
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / DAY_MS) + 1) / 7);
};

const getEmployeeName = (leave) => {
  if (leave?.employeeName) return leave.employeeName;
  const first = leave?.employee?.firstName || '';
  const last = leave?.employee?.lastName || '';
  const full = `${first} ${last}`.trim();
  return full || leave?.employeeId || 'Unknown';
};

const getMonthLabel = (date) =>
  date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }).toUpperCase();

const VacationPlanningReport = () => {
  const theme = useTheme();
  const today = new Date();
  const defaultMonth = toMonthInput(today);

  const [startMonth, setStartMonth] = useState(defaultMonth);
  const [endMonth, setEndMonth] = useState(defaultMonth);
  const [startWeek, setStartWeek] = useState('');
  const [endWeek, setEndWeek] = useState('');
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedProject, setSelectedProject] = useState('');

  const reportRef = useRef(null);
  const printRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    getAllEmployees()
      .then((data) => setEmployees(data || []))
      .catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;

    const updateScrollState = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [startMonth, endMonth, startWeek, endWeek, leaves.length, loading]);

  const scrollTableHorizontally = (direction) => {
    const el = tableScrollRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.floor(el.clientWidth * 0.55));
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const periodStart = useMemo(() => parseMonthInput(startMonth, false), [startMonth]);
  const periodEnd = useMemo(() => parseMonthInput(endMonth, true), [endMonth]);
  const invalidRange = Boolean(periodStart && periodEnd && periodStart > periodEnd);
  const startDateParam = useMemo(() => toIsoDateParam(periodStart), [periodStart]);
  const endDateParam = useMemo(() => toIsoDateParam(periodEnd), [periodEnd]);

  useEffect(() => {
    if (!startDateParam || !endDateParam || invalidRange) {
      setLeaves([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let isMounted = true;
    const fetchLeaves = async () => {
      setLoading(true);
      setError('');
      try {
        console.log('[VacationPlanningReport] Fetching with params:', { startDateParam, endDateParam });
        const data = await vacationPlanningData(startDateParam, endDateParam);
        console.log('[VacationPlanningReport] API response:', data?.length || 0, 'records');
        // console.log('vaction planning data', data);
        if (data?.length > 0) {
          console.log('[VacationPlanningReport] Sample record:', data[0]);
        }
        if (isMounted) setLeaves(data || []);
      } catch (e) {
        console.error('[VacationPlanningReport] API error:', e);
        if (isMounted) {
          setLeaves([]);
          setError('Failed to load vacation planning data.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchLeaves();

    return () => {
      isMounted = false;
    };
  }, [startDateParam, endDateParam, invalidRange]);

  const allDays = useMemo(() => {
    if (!periodStart || !periodEnd || periodStart > periodEnd) return [];
    const days = [];
    const curr = new Date(periodStart);
    while (curr <= periodEnd) {
      days.push(new Date(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return days;
  }, [periodStart, periodEnd]);

  const parsedStartWeek = useMemo(() => {
    const n = Number(startWeek);
    return Number.isInteger(n) && n >= 1 && n <= 53 ? n : null;
  }, [startWeek]);

  const parsedEndWeek = useMemo(() => {
    const n = Number(endWeek);
    return Number.isInteger(n) && n >= 1 && n <= 53 ? n : null;
  }, [endWeek]);

  const availableWeekNumbers = useMemo(() => {
    if (!allDays.length) return [];
    const seen = new Set();
    const numbers = [];
    allDays.forEach((day) => {
      const weekNo = getIsoWeek(day);
      if (!seen.has(weekNo)) {
        seen.add(weekNo);
        numbers.push(weekNo);
      }
    });
    return numbers;
  }, [allDays]);

  const weekIndexByNumber = useMemo(
    () => new Map(availableWeekNumbers.map((weekNo, idx) => [weekNo, idx])),
    [availableWeekNumbers]
  );

  useEffect(() => {
    if (startWeek && !weekIndexByNumber.has(Number(startWeek))) setStartWeek('');
    if (endWeek && !weekIndexByNumber.has(Number(endWeek))) setEndWeek('');
  }, [startWeek, endWeek, weekIndexByNumber]);

  const startWeekIndex = useMemo(() => {
    if (parsedStartWeek === null) return null;
    const idx = weekIndexByNumber.get(parsedStartWeek);
    return Number.isInteger(idx) ? idx : null;
  }, [parsedStartWeek, weekIndexByNumber]);

  const endWeekIndex = useMemo(() => {
    if (parsedEndWeek === null) return null;
    const idx = weekIndexByNumber.get(parsedEndWeek);
    return Number.isInteger(idx) ? idx : null;
  }, [parsedEndWeek, weekIndexByNumber]);

  const weekFilteredDays = useMemo(() => {
    if (!allDays.length) return [];
    return allDays.filter((day) => {
      const weekIdx = weekIndexByNumber.get(getIsoWeek(day));
      if (!Number.isInteger(weekIdx)) return false;
      if (startWeekIndex !== null && weekIdx < startWeekIndex) return false;
      if (endWeekIndex !== null && weekIdx > endWeekIndex) return false;
      return true;
    });
  }, [allDays, weekIndexByNumber, startWeekIndex, endWeekIndex]);

  const visibleDays = useMemo(() => {
    if (weekFilteredDays.length <= 1) return weekFilteredDays;
    const firstDay = weekFilteredDays[0];
    const secondDay = weekFilteredDays[1];
    const isSundayOnlyFirstWeek = firstDay.getDay() === 0 && getIsoWeek(firstDay) !== getIsoWeek(secondDay);
    return isSundayOnlyFirstWeek ? weekFilteredDays.slice(1) : weekFilteredDays;
  }, [weekFilteredDays]);

  const dayKeys = useMemo(() => visibleDays.map((d) => dateToKey(d)), [visibleDays]);
  const visibleDayKeysSet = useMemo(() => new Set(dayKeys), [dayKeys]);

  const monthSegments = useMemo(() => {
    if (!visibleDays.length) return [];
    const segments = [];
    let start = 0;
    let currentLabel = getMonthLabel(visibleDays[0]);
    for (let i = 1; i < visibleDays.length; i += 1) {
      const label = getMonthLabel(visibleDays[i]);
      if (label !== currentLabel) {
        segments.push({ label: currentLabel, start, end: i - 1 });
        start = i;
        currentLabel = label;
      }
    }
    segments.push({ label: currentLabel, start, end: visibleDays.length - 1 });
    return segments;
  }, [visibleDays]);

  const weekSegments = useMemo(() => {
    if (!visibleDays.length) return [];
    const segments = [];
    let start = 0;
    let currentWeek = getIsoWeek(visibleDays[0]);
    for (let i = 1; i < visibleDays.length; i += 1) {
      const wk = getIsoWeek(visibleDays[i]);
      if (wk !== currentWeek) {
        segments.push({ week: currentWeek, start, end: i - 1 });
        start = i;
        currentWeek = wk;
      }
    }
    segments.push({ week: currentWeek, start, end: visibleDays.length - 1 });
    return segments;
  }, [visibleDays]);

  const rows = useMemo(() => {
    if (!periodStart || !periodEnd || periodStart > periodEnd) return [];

    // Create employee map for quick lookup
    const employeeMap = new Map();
    (employees || []).forEach((emp) => {
      const empId = emp.employeeId || emp.id;
      if (empId) employeeMap.set(empId, emp);
    });

    const employeesData = {};

    (leaves || []).forEach((leave) => {
      const status = (leave?.status || '').toUpperCase();

      const leaveStart = normalizeDate(leave?.startDate);
      const leaveEnd = normalizeDate(leave?.endDate || leave?.startDate);
      if (!leaveStart || !leaveEnd) return;

      if (leaveEnd < periodStart || leaveStart > periodEnd) return;

      const employeeKey = leave?.employeeId || getEmployeeName(leave) || leave?.requestId;
      if (!employeesData[employeeKey]) {
        // Get employee from map
        const emp = employeeMap.get(leave?.employeeId);
        const officeName = emp?.office?.name || 'N/A';
        
        employeesData[employeeKey] = {
          employeeKey,
          name: getEmployeeName(leave),
          notPlanned: leave?.notPlanned || 0,
          approved: leave?.approved || 0,
          pending: leave?.pending || 0,
          department: emp?.department?.name || leave?.department || 'N/A',
          office: officeName,
          projectName: emp?.projectName || leave?.employee?.projectName || leave?.projectName || 'N/A',
          statusesByDay: {}
        };
      }

      const from = leaveStart < periodStart ? periodStart : leaveStart;
      const to = leaveEnd > periodEnd ? periodEnd : leaveEnd;

      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        if (d.getDay() === 0 || d.getDay() === 6) continue; // Sundays and Saturdays are non-working
        const key = dateToKey(d);
        const existing = employeesData[employeeKey].statusesByDay[key];
        if (!existing || STATUS_PRIORITY[status] < STATUS_PRIORITY[existing]) {
          employeesData[employeeKey].statusesByDay[key] = status;
        }
      }
    });

    return Object.values(employeesData)
      .filter((row) => Object.keys(row.statusesByDay).some((k) => visibleDayKeysSet.has(k)))
      .filter((row) => !selectedOffice || row.office === selectedOffice)
      .filter((row) => !selectedDepartment || row.department === selectedDepartment)
      .filter((row) => !selectedProject || row.projectName === selectedProject)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    leaves,
    employees,
    periodStart,
    periodEnd,
    visibleDayKeysSet,
    selectedOffice,
    selectedDepartment,
    selectedProject
  ]);

  const availableDepartments = useMemo(() => {
    const seen = new Set();
    (employees || []).forEach((emp) => {
      const deptName = emp?.department?.name;
      if (deptName) seen.add(deptName);
    });
    return Array.from(seen).sort();
  }, [employees]);

  const availableOffices = useMemo(() => {
    const seen = new Set();
    (employees || []).forEach((emp) => {
      const officeName = emp?.office?.name;
      if (officeName) seen.add(officeName);
    });
    const result = Array.from(seen).sort();
    console.log('[VacationPlanningReport] Total leaves:', leaves.length);
    console.log('[VacationPlanningReport] Available offices:', result);
    return result;
  }, [employees, leaves.length]);

  const availableProjects = useMemo(() => {
    const seen = new Set();
    (employees || []).forEach((emp) => {
      const project = emp?.projectName;
      if (project) seen.add(project);
    });
    (leaves || []).forEach((leave) => {
      const project = leave?.employee?.projectName || leave?.projectName;
      if (project) seen.add(project);
    });
    return Array.from(seen).sort();
  }, [employees, leaves]);

  const exportExcel = () => {
    if (!visibleDays.length) return;

    const leftCols = ['Department', 'Project', 'Employee'];
    const totalCols = leftCols.length + visibleDays.length;
    const aoa = [
      Array(totalCols).fill(''),
      Array(totalCols).fill(''),
      Array(totalCols).fill(''),
      Array(totalCols).fill('')
    ];

    aoa[0][0] = 'Department';
    aoa[0][1] = 'Project';
    aoa[0][2] = 'Members';
    aoa[0][3] = 'Not Planned';
    aoa[0][4] = 'Approved';
    aoa[0][5] = 'Pending';

    monthSegments.forEach((seg) => {
      aoa[0][leftCols.length + seg.start] = seg.label;
    });
    weekSegments.forEach((seg) => {
      aoa[1][leftCols.length + seg.start] = `WEEK ${seg.week}`;
    });
    visibleDays.forEach((day, idx) => {
      aoa[2][leftCols.length + idx] = day.getDate();
      aoa[3][leftCols.length + idx] = WEEKDAY_LETTERS[day.getDay()];
    });

    rows.forEach((row) => {
      const line = Array(totalCols).fill('');
      line[0] = row.department;
      line[1] = row.projectName;
      line[2] = row.name;
      line[3] = row.notPlanned;
      line[4] = row.approved;
      line[5] = row.pending;
      dayKeys.forEach((key, idx) => {
        const isWeekend = visibleDays[idx]?.getDay() === 0 || visibleDays[idx]?.getDay() === 6;
        const status = isWeekend ? null : row.statusesByDay[key];
        line[leftCols.length + idx] = status ? STATUS_META[status].letter : '';
      });
      aoa.push(line);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 3, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 3, c: 1 } },
      { s: { r: 0, c: 2 }, e: { r: 3, c: 2 } },
      ...monthSegments.map((seg) => ({
        s: { r: 0, c: leftCols.length + seg.start },
        e: { r: 0, c: leftCols.length + seg.end }
      })),
      ...weekSegments.map((seg) => ({
        s: { r: 1, c: leftCols.length + seg.start },
        e: { r: 1, c: leftCols.length + seg.end }
      }))
    ];
    ws['!cols'] = [
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
      ...visibleDays.map(() => ({ wch: 4 }))
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vacation Planning');
    XLSX.writeFile(
      wb,
      `Vacation_Planning_${startMonth || 'start'}_${endMonth || 'end'}.xlsx`
    );
  };

  const exportPdf = async () => {
    if (!printRef.current) return;
    
    const printContainer = printRef.current;
    
    try {
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: printContainer.scrollWidth,
        windowHeight: printContainer.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions for a single page PDF
      const imgW_px = canvas.width;
      const imgH_px = canvas.height;
      const ratio = imgW_px / imgH_px;
      
      // We'll use a standard landscape width (A3 = 420mm) and calculate height to fit
      const pdfWidth = 420;
      const pdfHeight = pdfWidth / ratio;
      
      const pdf = new jsPDF({
        orientation: ratio > 1 ? 'l' : 'p',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });
      
      // Add image to fit exactly to the custom page size
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      pdf.save(`Vacation_Planning_Report_${startMonth}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  const exportWord = () => {
    if (!reportRef.current) return;
    const html = `
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Vacation Planning</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 20px; }
          h2 { color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
          table { border-collapse: collapse; width: 100%; font-size: 10px; margin-top: 20px; }
          th, td { border: 1px solid #e2e8f0; padding: 6px; text-align: center; }
          th { background-color: #f8fafc; color: #475569; font-weight: bold; }
          .dept { text-align: left; background-color: #f1f5f9; font-weight: bold; }
          .name { text-align: left; font-weight: bold; color: #1e293b; }
          .pending { background-color: #fef3c7; color: #92400e; }
          .approved { background-color: #dcfce7; color: #166534; }
          .rejected { background-color: #fee2e2; color: #991b1b; }
          .sunday { background-color: #94a3b8; color: #ffffff; }
        </style>
      </head>
      <body>
        <h2>Vacation Planning Report</h2>
        <p><strong>Period:</strong> ${formatDate(periodStart)} to ${formatDate(periodEnd)}</p>
        <p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>
        ${reportRef.current.innerHTML}
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Vacation_Planning_${startMonth}.doc`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const invalidWeekRange = Boolean(
    startWeekIndex !== null && endWeekIndex !== null && startWeekIndex > endWeekIndex
  );

  const TableHeaderContent = ({ isPrint = false }) => (
    <>
      <TableHead>
        <TableRow>
          <TableCell
            rowSpan={4}
            sx={{ 
              minWidth: 160, 
              fontWeight: 800, 
              backgroundColor: '#f1f5f9',
              position: isPrint ? 'static' : 'sticky',
              top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
              left: isPrint ? 'auto' : 0,
              zIndex: isPrint ? 'auto' : 100,
              borderRight: '2px solid #e2e8f0'
            }}
          >
            Department
          </TableCell>
          <TableCell
            rowSpan={4}
            sx={{ 
              minWidth: 180, 
              fontWeight: 800, 
              backgroundColor: '#f1f5f9',
              position: isPrint ? 'static' : 'sticky',
              top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
              left: isPrint ? 'auto' : 160,
              zIndex: isPrint ? 'auto' : 100,
              borderRight: '2px solid #cbd5e1'
            }}
          >
            Project
          </TableCell>
          <TableCell
            rowSpan={4}
            sx={{ 
              minWidth: 240, 
              fontWeight: 800, 
              backgroundColor: '#f1f5f9',
              position: isPrint ? 'static' : 'sticky',
              top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
              left: isPrint ? 'auto' : 340,
              zIndex: isPrint ? 'auto' : 100,
              borderRight: '2px solid #cbd5e1'
            }}
          >
            Employee
          </TableCell>
          <TableCell
            rowSpan={4}
            sx={{ 
              minWidth: 40, 
              fontWeight: 800, 
              backgroundColor: '#f1f5f9',
              // position: isPrint ? 'static' : 'sticky',
              top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
              // left: isPrint ? 'auto' : 340,
              zIndex: isPrint ? 'auto' : 99,
              borderRight: '2px solid #cbd5e1'
            }}
          >
            N.P.
          </TableCell>
          <TableCell
            rowSpan={4}
            sx={{ 
              minWidth: 40, 
              fontWeight: 800, 
              backgroundColor: '#f1f5f9',
              // position: isPrint ? 'static' : 'sticky',
              top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
              // left: isPrint ? 'auto' : 340,
              zIndex: isPrint ? 'auto' : 99,
              borderRight: '2px solid #cbd5e1'
            }}
          >
            A
          </TableCell>
          <TableCell
            rowSpan={4}
            sx={{ 
              minWidth: 40, 
              fontWeight: 800, 
              backgroundColor: '#f1f5f9',
              // position: isPrint ? 'static' : 'sticky',
              top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
              // left: isPrint ? 'auto' : 340,
              zIndex: isPrint ? 'auto' : 99,
              borderRight: '2px solid #cbd5e1'
            }}
          >
            P
          </TableCell>
          {monthSegments.map((seg) => (
            <TableCell
              key={`m-${seg.label}-${seg.start}`}
              align="center"
              colSpan={seg.end - seg.start + 1}
              sx={{ 
                fontWeight: 800, 
                fontSize: '0.875rem', 
                color: '#1e40af', 
                backgroundColor: '#dbeafe',
                position: isPrint ? 'static' : 'sticky',
                top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[0]}px`,
                zIndex: isPrint ? 'auto' : 95,
                height: `${HEADER_ROW_HEIGHTS[0]}px`,
                py: 0,
                borderBottom: '1px solid #e2e8f0',
                letterSpacing: '0.05em'
              }}
            >
              {seg.label}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          {weekSegments.map((seg) => (
            <TableCell
              key={`w-${seg.week}-${seg.start}`}
              align="center"
              colSpan={seg.end - seg.start + 1}
              sx={{ 
                fontWeight: 700, 
                fontSize: '0.75rem',
                backgroundColor: '#ffffff',
                color: '#64748b',
                position: isPrint ? 'static' : 'sticky',
                top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[1]}px`,
                zIndex: isPrint ? 'auto' : 94,
                height: `${HEADER_ROW_HEIGHTS[1]}px`,
                py: 0,
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              WK {seg.week}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          {visibleDays.map((day, idx) => {
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            return (
              <TableCell
                key={`d-${dayKeys[idx]}`}
                align="center"
                sx={{
                  px: 0.5,
                  minWidth: 36,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  backgroundColor: isWeekend ? '#cbd5e1' : '#ffffff',
                  color: isWeekend ? '#475569' : '#1e293b',
                  position: isPrint ? 'static' : 'sticky',
                  top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[2]}px`,
                  zIndex: isPrint ? 'auto' : 93,
                  height: `${HEADER_ROW_HEIGHTS[2]}px`,
                  py: 0,
                  borderBottom: '1px solid #e2e8f0',
                  borderLeft: day.getDay() === 1 ? '1.5px solid #cbd5e1' : (day.getDay() === 0 ? '1px solid #cbd5e1' : '1px solid #e2e8f0')
                }}
              >
                {day.getDate()}
              </TableCell>
            );
          })}
        </TableRow>
        <TableRow>
          {visibleDays.map((day, idx) => {
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            return (
              <TableCell
                key={`l-${dayKeys[idx]}`}
                align="center"
                sx={{
                  px: 0.5,
                  minWidth: 36,
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  backgroundColor: isWeekend ? '#94a3b8' : '#fee2c7',
                  color: isWeekend ? '#ffffff' : '#b45309',
                  position: isPrint ? 'static' : 'sticky',
                  top: isPrint ? 'auto' : `${HEADER_ROW_TOPS[3]}px`,
                  zIndex: isPrint ? 'auto' : 92,
                  height: `${HEADER_ROW_HEIGHTS[3]}px`,
                  py: 0,
                  borderBottom: '2px solid #e2e8f0',
                  borderLeft: day.getDay() === 1 ? '1.5px solid #cbd5e1' : (day.getDay() === 0 ? '1px solid #cbd5e1' : '1px solid #e2e8f0')
                }}
              >
                {WEEKDAY_LETTERS[day.getDay()]}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.employeeKey} hover>
            <TableCell 
              sx={{ 
                fontWeight: 600, 
                backgroundColor: '#ffffff',
                position: isPrint ? 'static' : 'sticky',
                left: isPrint ? 'auto' : 0,
                zIndex: isPrint ? 'auto' : 90,
                borderRight: '2px solid #f1f5f9',
                fontSize: '0.8rem',
                color: '#64748b'
              }}
            >
              {row.department}
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 600, 
                backgroundColor: '#ffffff',
                position: isPrint ? 'static' : 'sticky',
                left: isPrint ? 'auto' : 160,
                zIndex: isPrint ? 'auto' : 90,
                borderRight: '2px solid #e2e8f0',
                fontSize: '0.8rem',
                color: '#64748b'
              }}
            >
              {row.projectName}
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 700, 
                backgroundColor: '#ffffff',
                position: isPrint ? 'static' : 'sticky',
                left: isPrint ? 'auto' : 340,
                zIndex: isPrint ? 'auto' : 90,
                borderRight: '2px solid #e2e8f0',
                fontSize: '0.85rem',
                color: '#1e293b'
              }}
            >
              {row.name}
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 700, 
                backgroundColor: '#ffffff',
                // position: isPrint ? 'static' : 'sticky',
                // left: isPrint ? 'auto' : 340,
                zIndex: isPrint ? 'auto' : 90,
                borderRight: '2px solid #e2e8f0',
                fontSize: '0.85rem',
                color: '#1e293b'
              }}
            >
              {row.notPlanned}
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 700, 
                backgroundColor: '#ffffff',
                // position: isPrint ? 'static' : 'sticky',
                // left: isPrint ? 'auto' : 340,
                zIndex: isPrint ? 'auto' : 90,
                borderRight: '2px solid #e2e8f0',
                fontSize: '0.85rem',
                color: '#1e293b'
              }}
            >
              {row.approved}
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 700, 
                backgroundColor: '#ffffff',
                // position: isPrint ? 'static' : 'sticky',
                // left: isPrint ? 'auto' : 340,
                zIndex: isPrint ? 'auto' : 90,
                borderRight: '2px solid #e2e8f0',
                fontSize: '0.85rem',
                color: '#1e293b'
              }}
            >
              {row.pending}
            </TableCell>
            {dayKeys.map((key, idx) => {
              const day = visibleDays[idx];
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const status = isWeekend ? null : row.statusesByDay[key];
              const meta = status ? STATUS_META[status] : null;
              
              return (
                <TableCell
                  key={`${row.employeeKey}-${key}`}
                  align="center"
                  sx={{
                    px: 0,
                    py: 0.75,
                    borderLeft: day.getDay() === 1 ? '1.5px solid #cbd5e1' : (day.getDay() === 0 ? '1px solid #cbd5e1' : '1px solid #f1f5f9'),
                    backgroundColor: isWeekend ? '#f1f5f9' : '#ffffff'
                  }}
                >
                  {meta ? (
                    <Tooltip title={`${meta.label}: ${row.name} (${formatDate(key)})`} arrow>
                      <Box
                        sx={{
                          mx: 'auto',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 900,
                          backgroundColor: meta.bg,
                          color: meta.color,
                          border: `1px solid ${meta.border}`,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          cursor: 'default'
                        }}
                      >
                        {meta.letter}
                      </Box>
                    </Tooltip>
                  ) : null}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh'}}>
      <PageHeader
        title="Vacation Planning"
        subtitle="Visual matrix of scheduled leaves and pending requests across departments"
        actions={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={18} />}
              onClick={exportExcel}
              disabled={loading || rows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Download size={18} />}
              onClick={exportPdf}
              disabled={loading || rows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<FileText size={18} />}
              onClick={exportWord}
              disabled={loading || rows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: theme.shadows[2] }}
            >
              Word
            </Button>
          </Stack>
        }
      />

      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, md: 2.5 }, 
          mb: 3, 
          borderRadius: 3, 
          border: '1px solid', 
          borderColor: 'divider',
          backgroundColor: '#ffffff',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.04)'
        }}
      >
        <Stack spacing={2.25}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(260px, 1fr))',
                xl: '1.1fr 1.8fr 1fr'
              },
              gap: 2
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <CalendarIcon size={18} color={theme.palette.text.secondary} />
                <Typography variant="body2" fontWeight="700" color="text.secondary">Period</Typography>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', gap: 1.25 }}>
                <TextField
                  label="From"
                  type="month"
                  value={startMonth}
                  onChange={(e) => setStartMonth(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                />
                <TextField
                  label="To"
                  type="month"
                  value={endMonth}
                  onChange={(e) => setEndMonth(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <Filter size={18} color={theme.palette.text.secondary} />
                <Typography variant="body2" fontWeight="700" color="text.secondary">Filters</Typography>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(120px, 1fr))' },
                  gap: 1.25
                }}
              >
                <TextField
                  label="Office"
                  select
                  value={selectedOffice}
                  onChange={(e) => setSelectedOffice(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <MenuItem value="">All Offices</MenuItem>
                  {availableOffices.map((office) => (
                    <MenuItem key={office} value={office}>{office}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Department"
                  select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {availableDepartments.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Project"
                  select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <MenuItem value="">All Projects</MenuItem>
                  {availableProjects.map((project) => (
                    <MenuItem key={project} value={project}>{project}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <CalendarDays size={18} color={theme.palette.text.secondary} />
                <Typography variant="body2" fontWeight="700" color="text.secondary">Weeks</Typography>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', gap: 1.25 }}>
                <TextField
                  label="Start Week"
                  select
                  value={startWeek}
                  onChange={(e) => setStartWeek(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <MenuItem value="">Full Period</MenuItem>
                  {availableWeekNumbers.map((weekNo) => (
                    <MenuItem key={`sw-${weekNo}`} value={`${weekNo}`}>
                      Week {weekNo}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="End Week"
                  select
                  value={endWeek}
                  onChange={(e) => setEndWeek(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <MenuItem value="">Full Period</MenuItem>
                  {availableWeekNumbers.map((weekNo) => (
                    <MenuItem key={`ew-${weekNo}`} value={`${weekNo}`}>
                      Week {weekNo}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Box>

          <Stack direction="row" spacing={3} sx={{ px: 1, flexWrap: 'wrap', rowGap: 1 }}>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <Stack key={key} direction="row" spacing={1} alignItems="center">
                <Box sx={{ 
                  width: 14, 
                  height: 14, 
                  borderRadius: '3px', 
                  backgroundColor: meta.bg, 
                  border: `1px solid ${meta.border}` 
                }} />
                <Typography variant="caption" fontWeight="600" color="text.secondary">
                  {meta.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {(invalidRange || invalidWeekRange) && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          {invalidRange ? 'Start month must be before or equal to end month.' : 'Start week must be before or equal to end week.'}
        </Alert>
      )}

      {error && (
        <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <CircularProgress size={40} thickness={4} />
          <Typography sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
            Generating planning matrix...
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{  height:'100%', borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#ffffff', 
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CalendarDays size={20} color={theme.palette.primary.main} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                {formatDate(periodStart).split(' ').slice(1).join(' ')} — {formatDate(periodEnd).split(' ').slice(1).join(' ')}
              </Typography>
            </Stack>
            
            <Stack direction="row" spacing={1}>
              <IconButton 
                size="small" 
                onClick={() => scrollTableHorizontally(-1)} 
                disabled={!canScrollLeft}
                sx={{ border: '1px solid #e2e8f0' }}
              >
                <ChevronLeft size={18} />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => scrollTableHorizontally(1)} 
                disabled={!canScrollRight}
                sx={{ border: '1px solid #e2e8f0' }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>
          </Box>

          {rows.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No leave records found for the selected period.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              ref={(node) => {
                reportRef.current = node;
                tableScrollRef.current = node;
              }}
              sx={{ 
                maxHeight: 'calc(100vh - 380px)',
                // height:'100%',
                backgroundColor: '#ffffff'
              }}
            >
              <Table size="small" stickyHeader sx={{ height:'100%'}}>
                <TableHeaderContent />
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Hidden Print Container for PDF Export */}
      <Box 
        ref={printRef} 
        sx={{ 
          position: 'absolute',
          top: -10000,
          left: -10000,
          width: 'max-content',
          minWidth: '297mm', // Minimum width for A4/A3 landscape
          backgroundColor: '#ffffff',
          zIndex: -1000
        }}
      >
        <Box sx={{ p: 4, backgroundColor: '#ffffff' }}>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 800, color: '#1e293b' }}>
            Vacation Planning Report
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: '#64748b' }}>
            {formatDate(periodStart)} — {formatDate(periodEnd)}
          </Typography>
          
          <Table size="small" sx={{ border: '1px solid #e2e8f0', width: 'max-content' }}>
            <TableHeaderContent isPrint={true} />
          </Table>
          
          <Box sx={{ mt: 4, display: 'flex', gap: 4 }}>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <Stack key={key} direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 16, height: 16, borderRadius: '4px', backgroundColor: meta.bg, border: `1px solid ${meta.border}` }} />
                <Typography variant="body2" fontWeight="700">{meta.label}</Typography>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 16, height: 16, borderRadius: '4px', backgroundColor: '#94a3b8' }} />
              <Typography variant="body2" fontWeight="700">Weekend</Typography>
            </Stack>
          </Box>
          
          <Typography variant="caption" sx={{ mt: 4, display: 'block', color: '#94a3b8' }}>
            Generated on {new Date().toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default VacationPlanningReport;
